import { isRouteErrorResponse } from 'react-router';
import { ACCESS_DENIED, ACCESS_DENIED_MESSAGE } from '~/constants';
import { getLogger } from '~/lib/logger/logger';
import { GenericError } from '../GenericError';
import { NotFound } from '../NotFound';
import { dataWithResponseInitSchema, getErrorPageUrl, isErrorPageType } from './GenericErrorBoundary.utils';
import { useTrackSearchError } from './useTrackSearchError';

const logger = getLogger('GenericErrorBoundary');

/**
 * Generic error boundary component to catch errors in React Router routes.
 *
 * To have your error caught in this Error Boundary, you need to throw a server
 * error from your loaders or actions. Make sure to pass the page type in the
 * `data` attribute @see ErrorPageTypes. We then display the relevant error page
 * from this boundary. (Unless your nested route has its own error boundary in
 * which case the error will be handled there).
 *
 * When an error is thrown from the client-side, React Router will type it as a
 * `DataWithResponseInit` error which we then parse into the same format.
 *
 * If you need to override the default description, pass your message in the
 * `statusText` when throwing the error. Eg:
 * ```
 * throw data(ErrorPageTypes.Collection, 404) // uses default message
 * throw data(ErrorPageTypes.Collection, { status: 404, statusText: 'My unique error message.' }) // custom message
 * ```
 */
export const GenericErrorBoundary = ({ error }: { error: unknown }) => {
  const routeError = isRouteErrorResponse(error) ? error : undefined;
  const { data: clientError } = dataWithResponseInitSchema.safeParse(error);

  useTrackSearchError(error, routeError?.status || clientError?.init?.status);

  /**
   * Errors that we have not manually thrown will be displayed as a generic error
   * (e.g. network errors, unexpected errors.
   */
  if (!routeError && !clientError) {
    const errorObject = error instanceof Error ? error : undefined;

    if (errorObject) {
      /**
       * Log unexpected errors here. For Route Errors and Data Errors, they
       * should be logged in the route loader or action before throwing the
       * error.
       */
      logger.error('Generic error occurred: ', {
        error: {
          // The error object won't be logged unless properties are accessed
          message: errorObject.message,
          stack: errorObject.stack,
        },
      });
    }

    return <GenericError />;
  }

  const errorData = routeError?.data || clientError?.data;
  const errorStatus = routeError?.status || clientError?.init?.status;
  const errorMessage = routeError?.statusText || clientError?.init?.statusText;

  const pageType = isErrorPageType(errorData) ? errorData : undefined;
  const notFoundUrl = getErrorPageUrl(pageType);

  // Add more error handling for specific status codes as needed
  switch (errorStatus) {
    case 404:
      return <NotFound type={pageType} linkTo={notFoundUrl} description={errorMessage} />;
    case 403:
      return <NotFound heading={ACCESS_DENIED} description={errorMessage || ACCESS_DENIED_MESSAGE} />;
    default:
      // All other errors eg. server errors or errors without a status code
      return <GenericError description={errorMessage} />;
  }
};
